import { VStack } from '@musinsa/mwms-ds'
import TitleSection from '@/components/content/TitleSection'
import { PageLayout } from '@/components/layout/PageLayout'
import { FormProvider, useForm } from 'react-hook-form'
import { getInitForm, PostPromotionListSchemaType } from './schemas/postPromotionListSchemas'
import {
  usePostBrandList,
  usePostMFSPartnerList,
  usePostPromotionList,
} from '@/services/settlement/settlementPromotion/useSettlementPromotionQueries'
import { useState } from 'react'
import { GRID_SIZE } from '@/utils/grid/grid'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { flushSync } from 'react-dom'
import { omit } from 'lodash-es'
import { MenuStorage } from '@/utils/storage/MenuStorage'
// LOGISPRD-10268 — 프로모션 고도화 화면(종류 2종·구간·시안 v4 레이아웃)
import { PromotionManagementForm } from './v2/components/PromotionManagementForm'
import { PromotionManagementTable } from './v2/components/PromotionManagementTable'
import { PROMOTION_KIND_TARGET_UNITS } from './v2/schemas/postPromotionSchemas'

function PromotionManagement() {
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<PostPromotionListSchemaType>()

  const form = useForm<PostPromotionListSchemaType>({ defaultValues: getInitForm(parsedQs) })

  const [currentPage, setCurrentPage] = useState(0)

  const [queryKey, setQueryKey] = useState<PostPromotionListSchemaType>(form.getValues())

  const values = form.watch()

  // LOGISPRD-11293 — 선택한 업체의 브랜드만 조회한다 (여기 조회분은 '전체 선택 = null' 정규화용, 잠금은 각 검색 폼)
  const { data: partnerBrands } = usePostBrandList({ partnerIds: values.partnerIds })
  const { data: partnerIds } = usePostMFSPartnerList()
  const brandOptionCount = partnerBrands?.length ?? 0

  // 전체 선택 = null 정규화. 미선택(빈 배열)도 전체로 본다 (LOGISPRD-10268 업체·브랜드·카테고리 조합)
  const normalizedPartnerIds = queryKey.partnerIds.length === 0 || partnerIds?.length === queryKey.partnerIds.length ? null : queryKey.partnerIds
  const normalizedBrandIds =
    queryKey.brandIds.length === 0 || (brandOptionCount > 0 && brandOptionCount === queryKey.brandIds.length) ? null : queryKey.brandIds

  // LOGISPRD-11309 — 종류는 화면 값이라 요청에 그대로 싣지 않는다. 적용 단위 목록으로 바꿔 보낸다
  const { promotionKind, ...searchCriteria } = queryKey

  // 프로모션 리스트 조회 쿼리
  const { data, refetch } = usePostPromotionList({
    ...searchCriteria,
    partnerIds: normalizedPartnerIds,
    brandIds: normalizedBrandIds,
    targetUnits: promotionKind ? PROMOTION_KIND_TARGET_UNITS[promotionKind] : null,
    page: currentPage,
    size: GRID_SIZE.SETTLEMENT,
  })

  const promotionList = data?.items ?? []

  const onChangePage = async (page: number) => {
    flushSync(() => setCurrentPage(page))

    await refetch()
  }

  const handleSearch = async (formData: PostPromotionListSchemaType) => {
    MenuStorage.menuOnSession.setSearch(formData)

    flushSync(() => {
      setQueryKey(formData)
      setCurrentPage(0)
    })

    reload()
    await refetch()
    replaceQuery(omit(formData, 'page', 'size'))
  }

  return (
    <FormProvider {...form}>
      <PageLayout gap={16}>
        <VStack gap={12}>
          <TitleSection variant="legacy" title="MFS 프로모션 관리" />
          <PromotionManagementForm onSearch={handleSearch} />
        </VStack>
        <PromotionManagementTable
          refetch={refetch}
          rowData={promotionList ?? []}
          pageInfo={data?.pageInfo ?? { page: 0, size: 0, totalCount: 0, totalPages: 0 }}
          onChangePage={onChangePage}
          isLoading={false}
        />
      </PageLayout>
    </FormProvider>
  )
}

export default PromotionManagement
